import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { listPlans } from "../../api/catalog";
import { listMembers } from "../../api/roster";
import { normalizeIndianMobile } from "../../api/login";
import {
  cancelInvite, createInvite, deliveryLabel, inviteLink, inviteState, isInviteEmail, listOrganisations, listSponsors, setApprovedPlans, setLimits, unlinkSponsor,
  updateOrganisation, type NewInvite, type Organisation, type OrganisationFields,
} from "../../api/organisations";
import { orgRefusal } from "./orgRefusal";
import { limitsSummary, OrganisationFieldsForm } from "./OrganisationsScreen";

/**
 * One organisation: its details, the plans its sponsor may give, its limits,
 * its sponsors and invites, and -- read only -- its members.
 *
 * 🔑 THE OPERATOR READS MEMBERS, IT DOES NOT CHANGE THEM (owner, §13.1): the
 * sponsor alone uploads, revokes and reinstates. This page has no member
 * action on purpose.
 *
 * Design: radius-wiki wiki/sponsor-roster-console-design.md §13.5.
 */
export function OrganisationPage() {
  const { orgId = "" } = useParams();
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  // No single-organisation GET exists: the list is small (a handful per
  // hotspot) and carries everything this page shows.
  const list = useQuery({
    queryKey: ["organisations", ns],
    queryFn: async () => (await listOrganisations(identity, ns)).data,
    enabled: !!ns,
  });
  const org = list.data?.organisations?.find((o) => o.organisation_id === orgId);

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Link component={RouterLink} to="/roster/organisations" variant="body2">← Organisations</Link>
      {list.isLoading && <Typography variant="body2" sx={{ mt: 2 }}>Loading…</Typography>}
      {list.isError && <Alert severity="error" sx={{ mt: 2 }}>{orgRefusal(list.error)}</Alert>}
      {list.data && !org && <Alert severity="warning" sx={{ mt: 2 }}>No organisation with this id on {ns}.</Alert>}
      {org && (
        <>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mt: 1, mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{org.name}</Typography>
            <Chip size="small" color={org.status === "active" ? "success" : "default"} label={org.status} />
          </Stack>
          <Stack spacing={2}>
            <DetailsCard ns={ns} org={org} />
            <PlansCard ns={ns} org={org} />
            <LimitsCard ns={ns} org={org} />
            <SponsorsCard ns={ns} org={org} />
            <MembersCard ns={ns} org={org} />
          </Stack>
        </>
      )}
    </Box>
  );
}

function Card({ title, children, note }: { title: string; children: React.ReactNode; note?: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {note && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{note}</Typography>}
      {children}
    </Paper>
  );
}

const fieldsOf = (o: Organisation): OrganisationFields => ({
  name: o.name,
  member_ref_label: o.member_ref_label,
  group_label_label: o.group_label_label,
  location_label_label: o.location_label_label,
});

function DetailsCard({ ns, org }: { ns: string; org: Organisation }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [f, setF] = useState<OrganisationFields>(fieldsOf(org));
  useEffect(() => setF(fieldsOf(org)), [org]);
  const dirty = JSON.stringify(f) !== JSON.stringify(fieldsOf(org));
  const save = useMutation({
    // The current status goes back with every edit -- see updateOrganisation.
    mutationFn: async (status: Organisation["status"]) => (await updateOrganisation(identity, ns, org.organisation_id, { ...f, name: f.name.trim() }, status)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["organisations", ns] }),
  });
  const suspended = org.status === "suspended";
  return (
    <Card title="Details">
      <OrganisationFieldsForm value={f} onChange={setF} />
      {save.isError && <Alert severity="error" sx={{ mt: 2 }}>{orgRefusal(save.error)}</Alert>}
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={!dirty || !f.name.trim() || save.isPending} onClick={() => save.mutate(org.status)}>
          Save
        </Button>
        <Button
          color={suspended ? "primary" : "warning"}
          disabled={dirty || save.isPending}
          title={dirty ? "Save or undo your edits first" : undefined}
          onClick={() => save.mutate(suspended ? "active" : "suspended")}
        >
          {suspended ? "Resume organisation" : "Suspend organisation"}
        </Button>
      </Stack>
      {suspended && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Suspended: its sponsor cannot upload and its members get no sponsored plan. Nothing is deleted.
        </Typography>
      )}
    </Card>
  );
}

function PlansCard({ ns, org }: { ns: string; org: Organisation }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["plans", ns], queryFn: async () => (await listPlans(identity, ns)).data, enabled: !!ns });
  const current = useMemo(() => [...(org.approved_plans ?? [])].sort(), [org.approved_plans]);
  const [picked, setPicked] = useState<string[]>(current);
  useEffect(() => setPicked(current), [current]);
  const save = useMutation({
    mutationFn: async () => (await setApprovedPlans(identity, ns, org.organisation_id, [...picked].sort())).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["organisations", ns] }),
  });
  const available = (plans.data ?? []).filter((p) => p.status !== false);
  // An approved plan that is no longer in the catalogue is still shown, so it
  // can be removed rather than silently kept.
  const names = Array.from(new Set([...available.map((p) => p.groupname), ...current])).sort();
  const label = (g: string) => {
    const p = available.find((x) => x.groupname === g);
    return p ? `${p.group_hd || g} (${g})` : `${g} — not in the catalogue`;
  };
  const dirty = JSON.stringify([...picked].sort()) !== JSON.stringify(current);
  return (
    <Card title="Approved plans" note="The plans this organisation's sponsor may give its members. Plans come from this hotspot's catalogue.">
      {plans.isLoading && <Typography variant="body2">Loading plans…</Typography>}
      {plans.isError && <Alert severity="error">{orgRefusal(plans.error)}</Alert>}
      {plans.data && names.length === 0 && <Typography variant="body2" color="text.secondary">This hotspot has no plans yet.</Typography>}
      <Stack>
        {names.map((g) => (
          <FormControlLabel
            key={g}
            control={
              <Checkbox
                size="small"
                checked={picked.includes(g)}
                onChange={(e) => setPicked(e.target.checked ? [...picked, g] : picked.filter((x) => x !== g))}
              />
            }
            label={label(g)}
          />
        ))}
      </Stack>
      {save.isError && <Alert severity="error" sx={{ mt: 1 }}>{orgRefusal(save.error)}</Alert>}
      <Button variant="contained" sx={{ mt: 1 }} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
        Save plans
      </Button>
    </Card>
  );
}

function LimitsCard({ ns, org }: { ns: string; org: Organisation }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const initial = () => ({
    speed: org.limits ? String(org.limits.max_speed_mbps) : "",
    days: org.limits ? String(org.limits.max_valid_days) : "",
    until: org.limits?.latest_valid_until ?? "",
  });
  const [v, setV] = useState(initial);
  useEffect(() => setV(initial()), [org.limits]);
  const speed = Number(v.speed);
  const days = Number(v.days);
  const valid = Number.isInteger(speed) && speed > 0 && Number.isInteger(days) && days > 0 && /^\d{4}-\d{2}-\d{2}$/.test(v.until);
  const save = useMutation({
    mutationFn: async () => (await setLimits(identity, ns, org.organisation_id, { max_speed_mbps: speed, max_valid_days: days, latest_valid_until: v.until })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["organisations", ns] }),
  });
  return (
    <Card
      title="Limits"
      note={`The most the sponsor may give on a row that names its own speed or validity. Now: ${limitsSummary(org)}. Required before the sponsor can use custom rows.`}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="Max speed (Mbps)" type="number" value={v.speed} onChange={(e) => setV({ ...v, speed: e.target.value })} />
        <TextField size="small" label="Max validity (days)" type="number" value={v.days} onChange={(e) => setV({ ...v, days: e.target.value })} />
        <TextField
          size="small"
          label="Latest end date"
          type="date"
          value={v.until}
          onChange={(e) => setV({ ...v, until: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button variant="contained" disabled={!valid || save.isPending} onClick={() => save.mutate()}>Save limits</Button>
      </Stack>
      {save.isError && <Alert severity="error" sx={{ mt: 1 }}>{orgRefusal(save.error)}</Alert>}
    </Card>
  );
}

function SponsorsCard({ ns, org }: { ns: string; org: Organisation }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [shown, setShown] = useState<NewInvite | null>(null);
  const q = useQuery({
    queryKey: ["orgSponsors", ns, org.organisation_id],
    queryFn: async () => (await listSponsors(identity, ns, org.organisation_id)).data,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["orgSponsors", ns, org.organisation_id] });
    void qc.invalidateQueries({ queryKey: ["organisations", ns] });
  };
  const cancel = useMutation({ mutationFn: async (id: string) => (await cancelInvite(identity, ns, org.organisation_id, id)).data, onSuccess: refresh });
  const unlink = useMutation({ mutationFn: async (subject: string) => (await unlinkSponsor(identity, ns, org.organisation_id, subject)).data, onSuccess: refresh });
  const sponsors = q.data?.sponsors ?? [];
  const invites = (q.data?.invites ?? []).filter((i) => i.status === "pending");

  return (
    <Card
      title="Sponsors"
      note="A sponsor is the organisation's own login: it uploads and revokes the people it pays for. Invite one by mobile; AnkhOS emails them the link when you give an email, and you can always send it yourself."
    >
      {q.isError && <Alert severity="error">{orgRefusal(q.error)}</Alert>}
      {(cancel.isError || unlink.isError) && <Alert severity="error" sx={{ mb: 1 }}>{orgRefusal(cancel.error ?? unlink.error)}</Alert>}
      {q.data && sponsors.length === 0 && invites.length === 0 && (
        <Typography variant="body2" color="text.secondary">No sponsor yet.</Typography>
      )}
      {(sponsors.length > 0 || invites.length > 0) && (
        <Table size="small" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Who</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Since</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sponsors.map((s) => (
              <TableRow key={s.subject}>
                <TableCell>
                  Login {s.subject}
                  {s.email && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {s.email} · weekly summary {s.digest ? "on" : "off"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell><Chip size="small" color="success" label="linked" /></TableCell>
                <TableCell>{s.created_at}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="warning"
                    disabled={unlink.isPending}
                    onClick={() => { if (window.confirm("Unlink this sponsor? Their login stays, but reaches this organisation no more.")) unlink.mutate(s.subject); }}
                  >
                    Unlink
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {invites.map((i) => (
              <TableRow key={i.invite_id}>
                <TableCell>
                  {i.full_name} · {i.mobile}
                  {i.email && <Typography variant="caption" color="text.secondary" component="div">{i.email}</Typography>}
                  {(["email", "sms"] as const).map((c) => {
                    const label = deliveryLabel(c, i.delivery?.[c]);
                    return label ? <Typography key={c} variant="caption" color="text.secondary" component="div">{label}</Typography> : null;
                  })}
                </TableCell>
                <TableCell><Chip size="small" color={inviteState(i).color} label={inviteState(i).label} /></TableCell>
                <TableCell>
                  {inviteState(i).when}
                  {inviteState(i).hint && <Typography variant="caption" color="text.secondary" component="div">{inviteState(i).hint}</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" disabled={cancel.isPending} onClick={() => cancel.mutate(i.invite_id)}>Cancel invite</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Button variant="outlined" disabled={org.status !== "active"} onClick={() => setInviting(true)}>Invite a sponsor</Button>
      {inviting && (
        <InviteDialog
          ns={ns}
          org={org}
          onClose={() => setInviting(false)}
          onCreated={(inv) => { setInviting(false); setShown(inv); refresh(); }}
        />
      )}
      {shown && <InviteLinkDialog invite={shown} onClose={() => setShown(null)} />}
    </Card>
  );
}

function InviteDialog({ ns, org, onClose, onCreated }: { ns: string; org: Organisation; onClose: () => void; onCreated: (i: NewInvite) => void }) {
  const identity = useIdentity();
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const normalised = normalizeIndianMobile(mobile);
  const emailBad = email.trim() !== "" && !isInviteEmail(email);
  const create = useMutation({
    mutationFn: async () =>
      (await createInvite(identity, ns, org.organisation_id, normalised ?? "", name.trim(), email, window.location.origin)).data,
    onSuccess: onCreated,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invite a sponsor for {org.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Mobile"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            error={!!mobile && !normalised}
            helperText={mobile && !normalised ? "An Indian mobile number, for example 98765 43210" : "They prove this number by SMS when they accept."}
            autoFocus
          />
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} slotProps={{ htmlInput: { maxLength: 128 } }} />
          <TextField
            label="Email (optional)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailBad}
            helperText={
              emailBad
                ? "One address, for example name@college.edu"
                : "We email them the link from no-reply@ankhos.io, and a weekly summary of their members once they accept."
            }
            slotProps={{ htmlInput: { maxLength: 254 } }}
          />
        </Stack>
        {create.isError && <Alert severity="error" sx={{ mt: 2 }}>{orgRefusal(create.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!normalised || !name.trim() || emailBad || create.isPending} onClick={() => create.mutate()}>
          Create invite
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * 🔑 THE LINK IS SHOWN ONCE. radcustomer keeps only its hash, so closing this
 * dialog loses it for good; the remedy is to cancel and invite again. Since
 * §13.8 AnkhOS also emails it (when an email was given) and will SMS it once
 * the DLT template exists -- the operator may still send it themselves.
 */
function InviteLinkDialog({ invite, onClose }: { invite: NewInvite; onClose: () => void }) {
  const link = inviteLink(window.location.origin, invite.token);
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send this link to {invite.full_name}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Shown once</AlertTitle>
          Copy it now. It cannot be shown again; if it is lost, cancel the invite and invite again.
        </Alert>
        <TextField value={link} fullWidth multiline slotProps={{ htmlInput: { readOnly: true } }} onFocus={(e) => e.target.select()} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Only {invite.mobile} can accept it, until {invite.expires_at}.
        </Typography>
        {invite.delivery?.email && (
          <Alert severity="info" sx={{ mt: 2 }}>
            We are also emailing this link to <b>{invite.email}</b> from no-reply@ankhos.io. The Sponsors list shows when it has
            gone.
          </Alert>
        )}
        {invite.delivery?.sms && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            An SMS to {invite.mobile} will follow once our SMS template is approved; until then, send the link yourself if you
            gave no email.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            void navigator.clipboard?.writeText(link).then(() => setCopied(true), () => setCopied(false));
          }}
        >
          {copied ? "Copied" : "Copy link"}
        </Button>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

function MembersCard({ ns, org }: { ns: string; org: Organisation }) {
  const identity = useIdentity();
  const q = useQuery({
    queryKey: ["orgMembers", ns, org.organisation_id],
    queryFn: async () => (await listMembers(identity, ns, { organisation_id: org.organisation_id })).data,
  });
  const members = q.data?.members ?? [];
  return (
    <Card
      title={`Members (${org.members.total})`}
      note={`${org.members.pending} not signed up · ${org.members.activated} signed up · ${org.members.revoked} revoked. The sponsor uploads and revokes; this view is read only.`}
    >
      {q.isError && <Alert severity="error">{orgRefusal(q.error)}</Alert>}
      {q.data && members.length === 0 && <Typography variant="body2" color="text.secondary">No members uploaded yet.</Typography>}
      {members.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mobile</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>{org.member_ref_label || "Member ID"}</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.mobile}</TableCell>
                  <TableCell>{m.full_name}</TableCell>
                  <TableCell>{m.member_ref}</TableCell>
                  <TableCell>{m.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {q.data?.next_cursor ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Showing the first page.</Typography>
          ) : null}
        </Box>
      )}
    </Card>
  );
}
